/**
 * InviteModal - Modal to invite a new team member
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { SUPER_ADMIN_ROLE_NAME } from '@upskill/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import invitationService, { Invitation, SendInvitationRequest } from '../../services/api/invitationService';
import roleService from '../../services/api/roleService';
import groupService from '../../services/api/groupService';
import userService from '../../services/api/userService';
import { useAuthStore } from '../../stores/authStore';
import { Check } from 'lucide-react';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: 2px;
`;

const SelectField = styled.select<{ $hasError?: boolean }>`
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  width: 100%;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const GroupChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const GroupToggleChip = styled.button<{ $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.medium};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.status.info.bg : theme.colors.backgrounds.white};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary.base : theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.fast};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
    color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const FooterNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: 0;

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const NoGroupsText = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[6]} 0;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const SuccessTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const SuccessSubtext = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

// ============================================================================
// Main Component
// ============================================================================

interface FormErrors {
  email?: string;
  roleId?: string;
}

export const InviteModal = ({ isOpen, onClose }: InviteModalProps) => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [createdInvitation, setCreatedInvitation] = useState<Invitation | null>(null);

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.getRoles({ limit: 100 }),
    enabled: isOpen,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupService.getGroups({ limit: 100 }),
    enabled: isOpen,
  });

  const { data: membersData } = useQuery({
    queryKey: ['members-all'],
    queryFn: () => userService.getMembers({ limit: 200 }),
    enabled: isOpen,
  });

  const sendInviteMutation = useMutation({
    mutationFn: (data: SendInvitationRequest) => invitationService.sendInvitation(data),
    onSuccess: (invitation) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      setCreatedInvitation(invitation);
    },
  });

  const handleClose = () => {
    setEmail('');
    setName('');
    setRoleId('');
    setSupervisorId('');
    setSelectedGroupIds([]);
    setErrors({});
    setCreatedInvitation(null);
    onClose();
  };

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!roleId) {
      newErrors.roleId = 'Please select a role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    sendInviteMutation.mutate({
      email: email.trim(),
      name: name.trim() || undefined,
      roleId,
      supervisorId: supervisorId || undefined,
      groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
    });
  };

  const roles = (rolesData?.data ?? []).filter(
    (r) => !(r.organizationId === null && r.name === SUPER_ADMIN_ROLE_NAME),
  );
  const groups = groupsData?.data ?? [];
  const members = membersData?.data ?? [];
  const orgName = user?.organizationId ? 'your organization' : 'Desert ABA Services';

  // Success state — show invite link for admin to copy & share
  if (createdInvitation) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Invitation Created" size="sm">
        <SuccessContainer>
          <SuccessIcon>
            <Check />
          </SuccessIcon>
          <SuccessTitle>Invitation sent to {createdInvitation.email}</SuccessTitle>
          <SuccessSubtext>
            We&apos;ve emailed them an invitation link to join your organization.
          </SuccessSubtext>
        </SuccessContainer>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            Done
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setCreatedInvitation(null);
              setEmail('');
              setName('');
              setRoleId('');
              setSupervisorId('');
              setSelectedGroupIds([]);
              setErrors({});
            }}
          >
            Invite Another
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite Team Member" size="sm">
      {/* Email */}
      <FormField>
        <FieldLabel>
          Email <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <Input
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
          }}
          error={errors.email}
          fullWidth
        />
      </FormField>

      {/* Name */}
      <FormField>
        <FieldLabel>Name</FieldLabel>
        <Input
          type="text"
          placeholder="Full name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
      </FormField>

      {/* Role */}
      <FormField>
        <FieldLabel>
          Role <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <SelectField
          value={roleId}
          onChange={(e) => {
            setRoleId(e.target.value);
            if (errors.roleId) setErrors((prev) => ({ ...prev, roleId: undefined }));
          }}
          $hasError={!!errors.roleId}
        >
          <option value="">Select a role...</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </SelectField>
        {errors.roleId && <ErrorText>{errors.roleId}</ErrorText>}
      </FormField>

      {/* Supervisor */}
      <FormField>
        <FieldLabel>Supervisor</FieldLabel>
        <SelectField value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
          <option value="">None</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.firstName} {m.lastName}
            </option>
          ))}
        </SelectField>
      </FormField>

      {/* Groups */}
      <FormField>
        <FieldLabel>Groups</FieldLabel>
        <GroupChipsContainer>
          {groups.map((g) => (
            <GroupToggleChip
              key={g.id}
              type="button"
              $selected={selectedGroupIds.includes(g.id)}
              onClick={() => toggleGroup(g.id)}
            >
              {!selectedGroupIds.includes(g.id) && '+ '}
              {g.name}
            </GroupToggleChip>
          ))}
          {groups.length === 0 && (
            <NoGroupsText>No groups available</NoGroupsText>
          )}
        </GroupChipsContainer>
      </FormField>

      <FooterNote>
        You'll get a link to share with them to join <strong>{orgName}</strong>.
      </FooterNote>

      <ModalFooter>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={sendInviteMutation.isPending}
          disabled={sendInviteMutation.isPending}
        >
          Send Invite
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InviteModal;
